import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Pagination } from "@/components/ui/Pagination";
import { Badge } from "@/components/ui/Badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Modal } from "@/components/ui/Modal";
import type { ErrorLogsResponse, ErrorLog } from "@/types";

function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

export function AdminErrorLogsPage() {
  const [data, setData] = useState<ErrorLogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [source, setSource] = useState("");
  const [errorClass, setErrorClass] = useState("");
  const [search, setSearch] = useState("");
  const [modalDetail, setModalDetail] = useState<ErrorLog | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), per_page: "25" });
    if (source) params.set("source", source);
    if (errorClass) params.set("error_class", errorClass);
    if (search) params.set("query", search);

    try {
      const res = await fetchWithAuth(`/admin/error_logs?${params}`);
      const json = await res.json();
      setData(json);
    } finally {
      setLoading(false);
    }
  }, [page, source, errorClass, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadDetail = async (id: number) => {
    const res = await fetchWithAuth(`/admin/error_logs/${id}`);
    const json = await res.json();
    setModalDetail(json);
    setCopied(false);
  };

  const formatForAi = (log: ErrorLog): string => {
    let contextStr = "";
    if (log.context) {
      try {
        contextStr = JSON.stringify(JSON.parse(log.context), null, 2);
      } catch {
        contextStr = log.context;
      }
    }

    return [
      `## Failed: ${log.error_class}`,
      `**Source:** ${log.source || "—"}`,
      `**Date:** ${formatDate(log.created_at)}`,
      `**Severity:** ${log.severity}`,
      "",
      "### Message",
      log.message || "—",
      "",
      "### Backtrace",
      log.backtrace || "—",
      "",
      "### Context",
      contextStr || "—",
    ].join("\n");
  };

  const handleCopyForAi = async () => {
    if (!modalDetail) return;
    await navigator.clipboard.writeText(formatForAi(modalDetail));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    await fetchWithAuth("/admin/error_logs/bulk_destroy", {
      method: "DELETE",
      body: JSON.stringify({ ids: Array.from(selectedIds) }),
    });
    setSelectedIds(new Set());
    loadData();
  };

  const handleDeleteAll = async () => {
    if (!confirm("Ar tikrai norite ištrinti visus klaidų įrašus?")) return;
    await fetchWithAuth("/admin/error_logs/destroy_all", {
      method: "DELETE",
    });
    setSelectedIds(new Set());
    loadData();
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data) return;
    if (selectedIds.size === data.error_logs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.error_logs.map((l) => l.id)));
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString("lt-LT");

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        ← Admin valdymo skydas
      </Link>
      <h1 className="text-2xl font-bold text-gray-900">Klaidų žurnalas</h1>

      {/* Stats */}
      {data && (
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-600">Iš viso</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {data.stats.total}
            </p>
          </div>
          <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-sm text-gray-600">Šiandien</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {data.stats.today}
            </p>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-gray-600">Unikalios klaidos</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {data.stats.unique}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Ieškoti..."
          className="input w-48"
        />
        <select
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setPage(1);
          }}
          className="input w-40"
        >
          <option value="">Visi šaltiniai</option>
          {data?.sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={errorClass}
          onChange={(e) => {
            setErrorClass(e.target.value);
            setPage(1);
          }}
          className="input w-48"
        >
          <option value="">Visos klasės</option>
          {data?.error_classes.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {selectedIds.size > 0 && (
          <button
            onClick={handleBulkDelete}
            className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Ištrinti ({selectedIds.size})
          </button>
        )}
        {data && data.stats.total > 0 && (
          <button
            onClick={handleDeleteAll}
            className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Ištrinti visus
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : data && data.error_logs.length > 0 ? (
        <>
          <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === data.error_logs.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Klasė
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Žinutė
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Šaltinis
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Data
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.error_logs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(log.id)}
                        onChange={() => toggleSelect(log.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => loadDetail(log.id)}
                        className="font-medium text-primary-800 hover:underline"
                      >
                        {log.error_class}
                      </button>
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-600">
                      {log.message || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {log.source ? (
                        <Badge
                          variant={log.source === "graphql" ? "info" : "default"}
                        >
                          {log.source}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(log.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.pagination && (
            <div className="mt-6">
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      ) : (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white py-12 text-center">
          <p className="text-gray-500">Klaidų nerasta</p>
        </div>
      )}

      {/* Error detail modal */}
      <Modal
        open={modalDetail !== null}
        onClose={() => setModalDetail(null)}
        title={modalDetail?.error_class ?? "Klaidos detalės"}
        size="4xl"
      >
        {modalDetail && (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto">
            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={modalDetail.severity === "error" ? "danger" : "warning"}>
                {modalDetail.severity}
              </Badge>
              {modalDetail.source && (
                <Badge variant={modalDetail.source === "graphql" ? "info" : "default"}>
                  {modalDetail.source}
                </Badge>
              )}
              <span className="text-sm text-gray-500">
                {formatDate(modalDetail.created_at)}
              </span>
            </div>

            {/* Message */}
            <div>
              <h3 className="text-sm font-medium text-gray-700">Žinutė</h3>
              <p className="mt-1 text-sm text-gray-900">
                {modalDetail.message || "—"}
              </p>
            </div>

            {/* Backtrace */}
            <div>
              <h3 className="text-sm font-medium text-gray-700">Backtrace</h3>
              <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
                {modalDetail.backtrace || "—"}
              </pre>
            </div>

            {/* Context */}
            <div>
              <h3 className="text-sm font-medium text-gray-700">Kontekstas</h3>
              <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
                {modalDetail.context
                  ? (() => {
                      try {
                        return JSON.stringify(JSON.parse(modalDetail.context), null, 2);
                      } catch {
                        return modalDetail.context;
                      }
                    })()
                  : "—"}
              </pre>
            </div>

            {/* Copy for AI button */}
            <div className="flex justify-end border-t border-gray-200 pt-3">
              <button
                onClick={handleCopyForAi}
                className="inline-flex items-center gap-2 rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
              >
                {copied ? "Nukopijuota!" : "Kopijuoti AI"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
